import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiJson } from "@/lib/api-client";
import { toast } from "sonner";

export type ClockifyTimeEntry = {
  id: string;
  clockify_id: string;
  project_id: string | null;
  project_group_id: string | null;
  person_id: string | null;
  clockify_user_id: string;
  description: string | null;
  start_time: string;
  end_time: string | null;
  duration_seconds: number | null;
  billable: boolean;
  synced_at: string;
  created_at: string;
  updated_at: string;
  people?: { first_name: string; last_name: string } | null;
};

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

interface ClockifyStatus {
  synced_projects: number;
  total_projects: number;
  synced_project_groups: number;
  total_project_groups: number;
  synced_users: number;
  total_employees: number;
  total_time_entries: number;
  last_sync: string | null;
}

interface OvertimeBalance {
  year: number;
  carry_over: number;
  months: Array<{
    month: number;
    work_days: number;
    public_holidays: number;
    absences_taken: number;
    employment_pct: number;
    expected_hours: number;
    actual_hours: number;
    delta: number;
  }>;
  year_delta: number;
  paid_out: number;
  balance: number;
  computed_until: string;
}

interface OvertimeSummary {
  year: number;
  employees: Array<{
    person_id: string;
    name: string;
    carry_over: number;
    actual_hours: number;
    paid_out: number;
    balance: number;
    last_entry: string | null;
  }>;
}

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useProjectTimeEntries(projectId: string | undefined) {
  const query = projectId
    ? appQueries.clockifyTimeEntries.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const entries = (
    (data ?? []) as Array<{
      id: string;
      clockifyID: string;
      projectID?: string | null;
      projectGroupID?: string | null;
      personID?: string | null;
      clockifyUserID: string;
      description?: string | null;
      startTime?: number | string | null;
      endTime?: number | string | null;
      durationSeconds?: number | null;
      billable: boolean;
      syncedAt?: number | string | null;
      createdAt?: number | string | null;
      updatedAt?: number | string | null;
      people?: { firstName: string; lastName: string } | null;
    }>
  ).map((entry) => ({
    id: entry.id,
    clockify_id: entry.clockifyID,
    project_id: entry.projectID ?? null,
    project_group_id: entry.projectGroupID ?? null,
    person_id: entry.personID ?? null,
    clockify_user_id: entry.clockifyUserID,
    description: entry.description ?? null,
    start_time: toLegacyTimestamp(entry.startTime),
    end_time: toLegacyTimestamp(entry.endTime) || null,
    duration_seconds: entry.durationSeconds ?? null,
    billable: entry.billable,
    synced_at: toLegacyTimestamp(entry.syncedAt),
    created_at: toLegacyTimestamp(entry.createdAt),
    updated_at: toLegacyTimestamp(entry.updatedAt),
    people: entry.people
      ? {
          first_name: entry.people.firstName,
          last_name: entry.people.lastName,
        }
      : null,
  }));

  return {
    data: entries,
    isLoading:
      !!projectId && details.type !== "complete" && entries.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
    refetch: () => {},
  };
}

export function useProjectGroupTimeEntries(projectGroupId: string | undefined) {
  const query = projectGroupId
    ? appQueries.clockifyTimeEntries.byProjectGroup({
        projectGroupID: projectGroupId,
      })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectGroupId });
  const entries = (
    (data ?? []) as Array<{
      id: string;
      clockifyID: string;
      projectID?: string | null;
      projectGroupID?: string | null;
      personID?: string | null;
      clockifyUserID: string;
      description?: string | null;
      startTime?: number | string | null;
      endTime?: number | string | null;
      durationSeconds?: number | null;
      billable: boolean;
      syncedAt?: number | string | null;
      createdAt?: number | string | null;
      updatedAt?: number | string | null;
      people?: { firstName: string; lastName: string } | null;
    }>
  ).map((entry) => ({
    id: entry.id,
    clockify_id: entry.clockifyID,
    project_id: entry.projectID ?? null,
    project_group_id: entry.projectGroupID ?? null,
    person_id: entry.personID ?? null,
    clockify_user_id: entry.clockifyUserID,
    description: entry.description ?? null,
    start_time: toLegacyTimestamp(entry.startTime),
    end_time: toLegacyTimestamp(entry.endTime) || null,
    duration_seconds: entry.durationSeconds ?? null,
    billable: entry.billable,
    synced_at: toLegacyTimestamp(entry.syncedAt),
    created_at: toLegacyTimestamp(entry.createdAt),
    updated_at: toLegacyTimestamp(entry.updatedAt),
    people: entry.people
      ? {
          first_name: entry.people.firstName,
          last_name: entry.people.lastName,
        }
      : null,
  }));

  return {
    data: entries,
    isLoading:
      !!projectGroupId && details.type !== "complete" && entries.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
    refetch: () => {},
  };
}

export function useClockifyStatus() {
  return useQuery({
    queryKey: ["clockify-status"],
    queryFn: () => apiJson<ClockifyStatus>("/api/clockify/status"),
  });
}

export function useOvertimeBalance(
  personId: string | undefined,
  year?: number,
) {
  return useQuery({
    queryKey: ["overtime", personId, year],
    queryFn: () => {
      const params = year ? `?year=${year}` : "";
      return apiJson<OvertimeBalance>(
        `/api/clockify/overtime/${personId}${params}`,
      );
    },
    enabled: !!personId,
    staleTime: 60_000,
  });
}

export function useOvertimeSummary(year?: number) {
  return useQuery({
    queryKey: ["overtime-summary", year],
    queryFn: () => {
      const params = year ? `?year=${year}` : "";
      return apiJson<OvertimeSummary>(
        `/api/clockify/overtime-summary${params}`,
      );
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useSyncProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<{ created: number; updated: number }>(
        "/api/clockify/sync-projects",
        { method: "POST" },
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clockify-status"] });
      toast.success(
        `Synced projects: ${data.created} created, ${data.updated} updated`,
      );
    },
    onError: (error) => {
      toast.error("Failed to sync projects: " + error.message);
    },
  });
}

export function useSyncUsers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<{ matched: number; total_employees: number }>(
        "/api/clockify/sync-users",
        { method: "POST" },
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clockify-status"] });
      toast.success(
        `Matched ${data.matched} of ${data.total_employees} employees`,
      );
    },
    onError: (error) => {
      toast.error("Failed to sync users: " + error.message);
    },
  });
}

export function useSyncTimeEntries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params?: { start?: string; end?: string }) =>
      apiJson<{ upserted: number }>("/api/clockify/sync-time-entries", {
        method: "POST",
        body: params ? JSON.stringify(params) : undefined,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clockify-status"] });
      queryClient.invalidateQueries({ queryKey: ["clockify-time-entries"] });
      toast.success(`Synced ${data.upserted} time entries`);
    },
    onError: (error) => {
      toast.error("Failed to sync time entries: " + error.message);
    },
  });
}

export function useComputeCarryOver() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (year: number) =>
      apiJson<{ computed: number; year: number }>(
        "/api/clockify/compute-carry-over",
        {
          method: "POST",
          body: JSON.stringify({ year }),
        },
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["overtime"] });
      queryClient.invalidateQueries({ queryKey: ["overtime-summary"] });
      toast.success(
        `Computed carry-over for ${data.computed} employees (${data.year})`,
      );
    },
    onError: (error) => {
      toast.error("Failed to compute carry-over: " + error.message);
    },
  });
}

export function useSetCarryOver() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      personId,
      year,
      hours,
    }: {
      personId: string;
      year: number;
      hours: number;
    }) =>
      apiJson<{ success: boolean }>(`/api/clockify/carry-over/${personId}`, {
        method: "PUT",
        body: JSON.stringify({ year, hours }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["overtime"] });
      queryClient.invalidateQueries({ queryKey: ["overtime-summary"] });
      toast.success("Carry-over updated");
    },
    onError: (error) => {
      toast.error("Failed to update carry-over: " + error.message);
    },
  });
}
